/**
 * Jain Gaurav Yatra — Announcements CRUD API.
 * Backend: /jain_gaurav_yatra/announcements/  (cookie-session auth + X-Tenant-ID).
 * Create uses multipart/form-data because `image` is an uploaded file.
 */
import { extractAdminTokens, getStoredAdminSession } from "@/lib/admin-login-api";

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";
}

function tenantId(): string {
  return (import.meta.env.VITE_X_TENANTID as string | undefined) ?? "";
}

/** Tenant + optional Bearer. No Content-Type so the browser sets the multipart boundary. */
function authHeaders(): HeadersInit {
  const id = tenantId();
  const { access } = extractAdminTokens(getStoredAdminSession()?.session);
  return {
    ...(id ? { "X-Tenant-ID": id } : {}),
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
  };
}

function errMessage(data: unknown, status: number): string {
  const d = data as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === "string" ? v : null);
  const msg =
    pick(d?.message) ??
    pick(d?.detail) ??
    (Array.isArray(d?.non_field_errors) ? pick((d.non_field_errors as string[])[0]) : null);
  if (msg) {return msg;}
  try {
    return JSON.stringify(data);
  } catch {
    return `Request failed (${status})`;
  }
}

const ANNOUNCEMENTS_PATH = "/jain_gaurav_yatra/announcements/";

/** Page-facing announcement shape. */
export interface Announcement {
  id: string;
  title: string;
  description: string;
  image_url: string | null;
  active: boolean;
  created_at: string;
}

/** Fields accepted when creating an announcement. */
export interface AnnouncementWrite {
  title: string;
  description: string;
  imageFile?: File | null;
}

function str(v: unknown): string {
  return v === null || v === undefined ? "" : String(v);
}

function toAnnouncement(row: Record<string, unknown>): Announcement {
  return {
    id: str(row.id),
    title: str(row.title),
    description: str(row.description),
    image_url: row.image ? str(row.image) : null,
    active: row.active !== false,
    created_at: str(row.created_at),
  };
}

function toFormData(p: AnnouncementWrite): FormData {
  const fd = new FormData();
  fd.append("title", p.title);
  fd.append("description", p.description);
  if (p.imageFile) {fd.append("image", p.imageFile);}
  return fd;
}

async function parseJson(res: Response): Promise<unknown> {
  return res.json().catch(() => ({}));
}

export async function listAnnouncements(): Promise<Announcement[]> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const res = await fetch(`${base}${ANNOUNCEMENTS_PATH}`, {
    method: "GET",
    headers: authHeaders(),
    credentials: "include",
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as { results?: unknown[] })?.results)
      ? (data as { results: unknown[] }).results
      : [];
  return (rows as Record<string, unknown>[]).map(toAnnouncement);
}

export async function createAnnouncement(payload: AnnouncementWrite): Promise<Announcement> {
  const base = apiBase();
  const res = await fetch(`${base}${ANNOUNCEMENTS_PATH}`, {
    method: "POST",
    headers: authHeaders(),
    credentials: "include",
    body: toFormData(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toAnnouncement(data as Record<string, unknown>);
}

export async function updateAnnouncement(
  id: string,
  payload: AnnouncementWrite,
): Promise<Announcement> {
  const base = apiBase();
  const res = await fetch(`${base}${ANNOUNCEMENTS_PATH}${id}/`, {
    method: "PATCH",
    headers: authHeaders(),
    credentials: "include",
    body: toFormData(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toAnnouncement(data as Record<string, unknown>);
}

export async function deleteAnnouncement(id: string): Promise<void> {
  const base = apiBase();
  const res = await fetch(`${base}${ANNOUNCEMENTS_PATH}${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok && res.status !== 204) {
    const data = await parseJson(res);
    throw new Error(errMessage(data, res.status));
  }
}
